/** Angular imports */
import {
    afterNextRender,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    OnInit,
    signal,
    TemplateRef,
    untracked,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, EMPTY, finalize, forkJoin, of } from 'rxjs';

/** Core imports */
import { ApiPagination, LicensesService, PlaylistsService, SocketService } from '@core';

/** Component imports */
import { SubNavbarService } from '@layouts/main-layout/components';
import {
    Actions,
    BaseQueryParams,
    ButtonComponent,
    SortOrder,
    StatsCardComponent,
    StatsData,
    TableCellClickPayload,
    TableComponent,
    TableFilter,
    TableToolbarComponent,
} from '@shared';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';

/** Local imports */
import { ApiPlaylist } from '@core/services/playlists/playlists.interface';
import { Logger, searchHandler, withLoading } from '@shared/utils';
import { PLAYLISTS_STATS, TECH_MAGIC_PLAYLIST_COLUMNS, TOOLBAR_BUTTONS } from './constants';
import { UiPlaylistTable } from './interfaces';

const API_FIELD_CREATED_AT = 'createdAt';
const API_FIELD_BUSINESS_NAME = 'clientInformation.businessName';

@Component({
    selector: 'app-playlists',
    imports: [
        TableToolbarComponent,
        TableComponent,
        StatsCardComponent,
        ButtonComponent,
        ConfirmationModalComponent,
    ],
    templateUrl: './playlists.component.html',
    styleUrl: './playlists.component.scss',
})
export class PlaylistsComponent implements OnInit {
    /** Handles navigation and routing logic. */
    private router = inject(Router);

    /** Manages WebSocket connection and message handling. */
    private socketService = inject(SocketService);

    /** Manages content projection for the sub-navbar. */
    private subNavbarService = inject(SubNavbarService);

    /** Manages playlist data operations and API interactions. */
    private playlistsService = inject(PlaylistsService);

    /** Resolves license names (alias/key) for the Push Updates confirmation caption. */
    private licensesService = inject(LicensesService);

    /** Clean up subscriptions and resources when the component is destroyed. */
    private destroyRef = inject(DestroyRef);

    /** Template reference to be projected into the sub-navbar component */
    private subNavBarContent = viewChild<TemplateRef<any>>('subNavBarContent');

    /** Playlists stats signal for holding live and initial data */
    protected playlistsStats = signal<StatsData[]>(PLAYLISTS_STATS);

    /** Mapped playlist data for the table */
    protected playlistData = signal<UiPlaylistTable[]>([]);

    /**
     * Fixed page size for playlist requests. Kept separate from
     * `pagination.limit` because that signal gets overwritten by whatever
     * `meta.pagination` the backend echoes back on every fetch — using it
     * directly for "Show Less" truncation would silently drift if the server
     * ever reports a different limit than requested.
     */
    protected readonly pageSize = 10;

    /** Playlist table toolbar buttons */
    protected toolbarButtons = TOOLBAR_BUTTONS;

    /** Playlist table columns */
    protected tableColumns = TECH_MAGIC_PLAYLIST_COLUMNS;

    /** Flag indicating whether table filtering is pending */
    protected isFiltering = signal<boolean>(false);
    protected isDeletingPlaylist = signal<boolean>(false);
    protected isLoadingPlaylistStats = signal<boolean>(false);
    protected showDeleteConfirmationModal = signal<boolean>(false);
    protected showResultModal = signal<boolean>(false);
    /** Starts true so the table skeleton shows immediately on first visit. */
    protected isLoadingPlaylists = signal<boolean>(true);
    protected selectedPlaylistToDelete = signal<UiPlaylistTable | null>(null);

    /** Visibility of the Push Updates confirmation modal. */
    protected showPushUpdatesModal = signal<boolean>(false);

    /** Playlist row pending a Push Updates broadcast. */
    protected selectedPlaylistToPushUpdates = signal<UiPlaylistTable | null>(null);

    /** Resolved license names (alias/key) for the selected Push Updates row. */
    protected pushUpdatesLicenseNames = signal<string[]>([]);

    /** True while license names are being resolved before the modal can confirm. */
    protected isLoadingPushUpdatesLicenses = signal<boolean>(false);

    /**
     * Caption shown inside the Push Updates confirmation modal.
     * Substitutes the resolved license names (alias/key) into the prompt so the
     * user sees exactly which licenses will receive the broadcast.
     */
    protected pushUpdatesMessage = computed<string>(() => {
        const names = this.pushUpdatesLicenseNames();
        if (this.isLoadingPushUpdatesLicenses()) {
            return 'Resolving licenses...';
        }
        if (names.length === 0) {
            return 'This will push the latest updates to all devices on this license. Devices may restart during the update. Do you want to continue?';
        }
        const quoted = names.map((name) => `"${name}"`).join(', ');
        return `This will push the latest updates to ${quoted}. Devices may restart during the update. Do you want to continue?`;
    });

    /** Tracks "Show More" requests so the next table fetch appends rather than replaces. */
    private isPaginationRequest = signal<boolean>(false);

    /** Loading indicator for "Show More" fetches; rendered as a table overlay. */
    protected isPaginating = signal<boolean>(false);

    /** Confirmation modal configuration for success or error messages */
    protected modalConfig = signal<{
        type: 'success' | 'error';
        title: string;
        message: string;
    }>({
        type: 'success',
        title: '',
        message: '',
    });

    /**
     * When true, closing the result modal should refetch the playlists table.
     * Used by flows that mutate the list (e.g. delete); kept false for flows that
     * don't (e.g. Push Updates) so the modal close doesn't trigger an API call.
     */
    private resultModalShouldRefetch = signal<boolean>(false);

    /** Holds the debounced search key value for searching playlist by dealer name */
    protected searchKey = signal<string>('');

    /**
     * Pagination configuration for the users module.
     * Controls the current page, page size limit
     */
    protected pagination = signal<ApiPagination>({
        hasNextPage: true,
        hasPreviousPage: false,
        limit: this.pageSize,
        page: 1,
        totalCount: 0,
        totalPages: 0,
    });

    /** Holds the current table state including filters, sorting and pagination */
    protected tableState = signal<TableFilter>({
        filters: {},
        page: 1,
        pageSize: this.pageSize,
        sortField: 'createdAt',
        sortOrder: SortOrder.DESC,
    });

    constructor() {
        // Handles projecting content into the sub-navbar component after view is initialized
        afterNextRender(() => {
            const content = this.subNavBarContent();
            if (content) {
                this.subNavbarService.setContent(content);
            }
        });

        // Load stats once on init; playlist rows are loaded via tableState searchHandler.
        effect(() => {
            untracked(() => this.loadPlaylistStats());
        });

        /**
         * Search playlist by dealer name, dealer name for now since API currently does not support OR operation
         */
        searchHandler(
            this.searchKey,
            (searchKey) => {
                return this.playlistsService.searchPlaylistByDealerName(searchKey).pipe(
                    withLoading(this.isLoadingPlaylists),
                    catchError((e: unknown) => {
                        Logger.error('PlaylistComponent', 'Failed to search playlists', e);
                        return EMPTY;
                    }),
                    takeUntilDestroyed(this.destroyRef),
                );
            },
            this.destroyRef,
            {
                next: (res) => {
                    this.pagination.set(res.meta.pagination);
                    this.mapDataToUi(res.data);
                },
                onEmpty: () => {
                    const state = this.tableState();
                    if (state) {
                        this.tableState.set({ ...state });
                    }
                },
            },
        );

        /**
         * Handles table state changes with debouncing
         */
        searchHandler(
            this.tableState,
            (state) => {
                const isPaginating = this.isPaginationRequest();
                const hasColumnFilters = Object.values(state.filters).some(
                    (value) => value !== undefined && value !== null && value !== '',
                );

                if (!isPaginating) {
                    this.isFiltering.set(hasColumnFilters);
                }

                const loadingSignal = isPaginating ? this.isPaginating : this.isLoadingPlaylists;

                return this.playlistsService.getPlaylists(this.constructQueryParams(state)).pipe(
                    withLoading(loadingSignal),
                    catchError((e: unknown) => {
                        Logger.error('PlaylistComponent', 'Failed to load filtered playlists', e);
                        return EMPTY;
                    }),
                    finalize(() => {
                        this.isPaginating.set(false);
                        this.isFiltering.set(false);
                    }),
                    takeUntilDestroyed(this.destroyRef),
                );
            },
            this.destroyRef,
            {
                next: (res) => {
                    const append = this.isPaginationRequest();
                    if (append) this.isPaginationRequest.set(false);
                    this.pagination.set(res.meta.pagination);
                    this.mapDataToUi(res.data, append);
                },
            },
            {
                debounceTime: 0,
                isEmpty: () => false,
            },
        );

        this.destroyRef.onDestroy(() => {
            this.subNavbarService.clearContent();
        });
    }

    /** Open socket connection */
    ngOnInit(): void {
        this.socketService.connect();
    }

    /**
     * Opens the playlist creation interface in a new browser tab.
     */
    protected onCreatePlaylist(): void {
        const route = this.router.url;

        window.open(`${route}/create`, '_blank');
    }

    /**
     * Opens the details view for a specific row in a new tab.
     * @param event - The payload containing the clicked cell data and row identifier.
     */
    protected onCellClick(event: TableCellClickPayload<string, UiPlaylistTable>): void {
        const route = this.router.url;

        window.open(`${route}/${event.row.id}`, '_blank');
    }

    /**
     * Handles table state changes, including pagination and filtering.
     * @param event - The table state change payload.
     */
    protected onTableStateChange(event: TableFilter): void {
        const current = this.tableState();
        if (this.isSameTableState(current, event)) {
            return;
        }
        this.tableState.set(event);
    }

    /**
     * Marks the next table fetch as a "Show More" pagination request so rows are appended.
     */
    protected onShowMoreRequested(): void {
        this.isPaginationRequest.set(true);
    }

    /**
     * Collapses the accumulated playlist rows back to the first page.
     * The table's own pagination reset (triggered alongside this) re-syncs
     * `tableState.page` for us via `tableStateChange`, so only local state and
     * our own `pagination` bookkeeping need resetting here.
     */
    protected onShowLess(): void {
        this.playlistData.update((current) => current.slice(0, this.pageSize));
        this.pagination.update((p) => ({ ...p, page: 1 }));
    }

    /**
     * Orchestrates row-level operations based on the provided action type.
     * Manages asynchronous workflows for features like screen capture and data removal.
     * @param payload - Data containing the action identifier and the target row object.
     */
    protected async onTableAction(payload: {
        action: string;
        row: UiPlaylistTable;
    }): Promise<void> {
        switch (payload.action.toLowerCase()) {
            case Actions.DELETE:
                this.selectedPlaylistToDelete.set(payload.row);
                this.showDeleteConfirmationModal.set(true);
                break;
            case Actions.PUSH_UPDATES: {
                const assigned = payload.row.assignedLicenses ?? [];
                if (assigned.length === 0) {
                    Logger.error('PlaylistComponent', 'No license Id found');
                    this.modalConfig.set({
                        type: 'error',
                        title: 'No Licenses Assigned',
                        message: `'${payload.row.playlistName}' has no licenses assigned. Assign a license before pushing updates.`,
                    });
                    this.resultModalShouldRefetch.set(false);
                    this.showResultModal.set(true);
                    break;
                }
                this.selectedPlaylistToPushUpdates.set(payload.row);
                this.showPushUpdatesModal.set(true);
                this.resolvePushUpdatesLicenseNames(assigned);
                break;
            }
            default:
                break;
        }
    }

    /**
     * Handles the confirmation of the playlist deletion.
     */
    protected onConfirmDelete(): void {
        const playlist = this.selectedPlaylistToDelete();
        if (playlist) {
            this.deletePlaylistById(playlist.id);
        }
    }

    /**
     * Handles closing the result modal and reloads the playlist data
     */
    protected onResultModalClose(): void {
        this.showResultModal.set(false);
        if (this.resultModalShouldRefetch()) {
            this.resultModalShouldRefetch.set(false);
            this.tableState.set({ ...this.tableState() });
            this.loadPlaylistStats();
        }
    }

    /**
     * Handles the cancellation of the playlist deletion.
     */
    protected onCancelDelete(): void {
        this.showDeleteConfirmationModal.set(false);
        this.selectedPlaylistToDelete.set(null);
    }

    /**
     * Confirms the Push Updates action and broadcasts the content update
     * to every license assigned to the selected playlist.
     */
    protected onConfirmPushUpdates(): void {
        const playlist = this.selectedPlaylistToPushUpdates();
        const licenses = playlist?.assignedLicenses ?? [];

        if (!playlist || licenses.length === 0) {
            Logger.error('PlaylistComponent', 'No license Id found');
            this.modalConfig.set({
                type: 'error',
                title: 'Push Updates Failed',
                message: 'This playlist has no assigned licenses to push updates to.',
            });
            this.showPushUpdatesModal.set(false);
            this.selectedPlaylistToPushUpdates.set(null);
            this.pushUpdatesLicenseNames.set([]);
            this.showResultModal.set(true);
            return;
        }

        for (const licenseId of licenses) {
            this.socketService.sendRaspiContentsUpdate(licenseId);
        }

        const licenseLabel = licenses.length === 1 ? 'license' : 'licenses';
        this.modalConfig.set({
            type: 'success',
            title: 'Push Updates Sent',
            message: `Latest updates pushed to ${licenses.length} ${licenseLabel} for '${playlist.playlistName}'.`,
        });
        this.showPushUpdatesModal.set(false);
        this.selectedPlaylistToPushUpdates.set(null);
        this.pushUpdatesLicenseNames.set([]);
        this.showResultModal.set(true);
    }

    /**
     * Cancels the Push Updates confirmation modal.
     */
    protected onCancelPushUpdates(): void {
        this.showPushUpdatesModal.set(false);
        this.selectedPlaylistToPushUpdates.set(null);
        this.pushUpdatesLicenseNames.set([]);
    }

    /**
     * Resolves the alias/license-key for each id assigned to the Push Updates row
     * and stores the human-readable names so the confirmation caption can show
     * them instead of bare ids.
     *
     * @param licenseIds - License ids assigned to the playlist row.
     */
    private resolvePushUpdatesLicenseNames(licenseIds: string[]): void {
        this.pushUpdatesLicenseNames.set([]);

        if (!licenseIds.length) return;

        this.isLoadingPushUpdatesLicenses.set(true);

        forkJoin(
            licenseIds.map((id) =>
                this.licensesService.getLicenseById(id).pipe(catchError(() => of(null))),
            ),
        )
            .pipe(
                finalize(() => this.isLoadingPushUpdatesLicenses.set(false)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (licenses) => {
                    const names = licenses.map((license, index) => {
                        if (!license) return licenseIds[index];
                        return license.alias || license.licenseKey || licenseIds[index];
                    });
                    this.pushUpdatesLicenseNames.set(names);
                },
            });
    }

    /**
     * Constructs the query parameters for the API based on the table filter state.
     * Maps UI field names to API field names and formats filters.
     *
     * @param state - The current table filter state.
     * @returns The constructed BaseQueryParams object.
     */
    /**
     * Compares two table states to avoid redundant API calls when the table
     * re-emits its initial state on mount.
     */
    private isSameTableState(current: TableFilter, next: TableFilter): boolean {
        return (
            current.page === next.page &&
            current.pageSize === next.pageSize &&
            current.sortField === next.sortField &&
            current.sortOrder === next.sortOrder &&
            JSON.stringify(current.filters) === JSON.stringify(next.filters)
        );
    }

    private constructQueryParams(state: TableFilter): BaseQueryParams {
        const filters: Record<string, any> = {};

        // Map UI filter fields to API fields
        Object.entries(state.filters).forEach(([key, value]) => {
            if (!value) return;

            let apiKey = key;
            if (key === 'publishedDate') apiKey = API_FIELD_CREATED_AT;
            if (key === 'assignedTo') apiKey = API_FIELD_BUSINESS_NAME;

            filters[apiKey] = { _ilike: value };
        });

        // Map UI sort field to API field
        let sortBy = state.sortField;
        if (sortBy === 'publishedDate') sortBy = API_FIELD_CREATED_AT;
        if (sortBy === 'assignedTo') sortBy = API_FIELD_BUSINESS_NAME;

        return {
            page: state.page,
            limit: this.pageSize,
            sortBy: sortBy || 'createdAt',
            sortOrder: state.sortOrder,
            filters: Object.keys(filters).length > 0 ? filters : undefined,
        };
    }

    /**
     * Maps the raw API playlist response into the UI table row format.
     * Extracts and transforms properties to match the expected table column fields:
     * `playlistName`, `publishedDate`, `assignedTo`, and `totalContent`.
     *
     * @param data The array of raw API playlist objects
     * @param append When true, appends mapped rows to the existing table data instead of replacing it.
     */
    private mapDataToUi(data: ApiPlaylist[], append = false): void {
        const mapped = data.map((playlist) => ({
            id: playlist.playlistId,
            playlistName: playlist.playlistName,
            publishedDate: playlist.createdAt
                ? new Date(playlist.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                  })
                : '--',
            assignedTo: playlist.clientInformation?.businessName ?? '--',
            totalContent: playlist.totalContent ?? 0,
            assignedLicenses: playlist.assignedLicenses ?? [],
        }));

        if (append) {
            this.playlistData.update((prev) => [...prev, ...mapped]);
        } else {
            this.playlistData.set(mapped);
        }
    }

    /** ================================ API Calls ===================================== */

    /**
     * Loads playlist stats map to the PLAYLISTS_STATS constant
     */
    private loadPlaylistStats(): void {
        this.playlistsService
            .getPlaylistsStats()
            .pipe(
                withLoading(this.isLoadingPlaylistStats),
                catchError((e: unknown) => {
                    Logger.error('PlaylistComponent', 'Failed to load playlists stats', e);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (res) => {
                    const stats = res.data?.totalPlaylists;
                    if (!stats) return;
                    const mapped = PLAYLISTS_STATS.map((template) => {
                        const stat = { ...template };

                        if (stat.title === 'Playlist(s)') {
                            stat.total = stats.overall?.toString() ?? '0';
                        } else if (stat.title === 'New this week') {
                            stat.total = (stats.thisWeek ?? stats.thisWeek ?? 0).toString();
                        } else if (stat.title === 'New last week') {
                            stat.total = (stats.lastWeek ?? stats.lastWeek ?? 0).toString();
                        }

                        return stat;
                    });

                    this.playlistsStats.set(mapped);
                },
            });
    }

    /**
     * Deletes playlist by its playlist id
     */
    private deletePlaylistById(playlistId: string): void {
        this.playlistsService
            .deletePlaylistById(playlistId)
            .pipe(
                withLoading(this.isDeletingPlaylist),
                catchError((e: unknown) => {
                    Logger.error('PlaylistComponent', 'Failed to delete playlist', e);
                    this.modalConfig.set({
                        type: 'error',
                        title: 'Delete Failed',
                        message: 'An error occurred while deleting the playlist. Please try again.',
                    });
                    this.showDeleteConfirmationModal.set(false);
                    this.resultModalShouldRefetch.set(false);
                    this.showResultModal.set(true);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: () => {
                    this.modalConfig.set({
                        type: 'success',
                        title: 'Playlist Deleted',
                        message: 'The playlist has been successfully deleted.',
                    });
                    this.showDeleteConfirmationModal.set(false);
                    this.resultModalShouldRefetch.set(true);
                    this.showResultModal.set(true);
                    this.selectedPlaylistToDelete.set(null);
                },
            });
    }
}
